/**
 * Travel Desk trip lifecycle (ADR 0030, plan §5.2). Pure and RN-safe.
 *
 * One table of legal status moves, shared by the API, the worker jobs and the screens, so a
 * button the web shows is a move the server accepts. An illegal move is a 409. The calendar
 * phase (upcoming / travelling / returned) is derived from the trip's dates, never stored.
 */

import type { TravelTripPhase, TravelTripStatus } from '../enums.js';
import { ConflictError } from '../errors.js';

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * The legal moves: plan §5.2 plus the moves R-P10's shipped routes already make (D36: the shipped,
 * tested behaviour wins), so one writer (`setTripStatus` in the API) can assert every status write:
 *
 * - `invited` — a colleague added this person to a trip (D10). Accepting makes it their draft;
 *   declining withdraws it.
 * - `draft` → `pending` on submit. Asking and answering a question keeps the trip `pending`
 *   (the workflow pauses, the status does not move). A draft the traveller abandons is
 *   `withdrawn`. When the approval workflow finishes inside the submit call (an org owner, or a
 *   traveller nobody needs to approve) the trip goes straight from `draft` to `approved`, or on
 *   to `booked` (self-booked) or `settling` (post-facto) — the same three exits as an approval.
 * - `pending` → `approved`, `rejected`, or `withdrawn` by the traveller; the last approval also
 *   lands a self-booked trip on `booked` and a post-facto trip on `settling` (already travelled).
 * - `rejected` → `pending` / `approved` / `booked` / `settling`: R-P10's shipped reopen. An
 *   approver with authority may approve a rejected request, which reopens its workflow instance
 *   and then decides it; where it lands depends on whether more steps remain. There is no
 *   override to force-cancel or to pass a hard block (D24 is dropped, EC-53).
 * - `approved` → `booked` when the desk has booked every item, or directly for a self-booked
 *   trip; → `settling` for a post-facto trip; → `needs_action` when a booking is declined, an
 *   option expires or the desk overspends; → `expired` when the end date passes and nothing was
 *   ever booked; → `cancelled`.
 * - `needs_action` ⇄ `booked`: resolved returns to booked; a declined / overspent change on a
 *   booked trip comes back here. It can also be cancelled, or expire if it was never booked
 *   (the `travel.expire_unbooked` job checks `bookedAt`, this table only allows the move), or
 *   move to `settling` when the trip ends while it was still flagged: a booked trip that dropped
 *   here (declined item, expired option, overspend) has still been travelled, so the end-of-trip
 *   job must be able to start settlement from `needs_action` (the job checks `bookedAt`).
 * - `booked` → `settling` when the trip ends, or `cancelled`. Changes to a booked trip are
 *   amendments and do not move the trip's status.
 * - `settling` → `closed` when the expense window ends and nothing is outstanding.
 *
 * `withdrawn`, `cancelled`, `expired` and `closed` are final. `rejected` is final for the
 * traveller and the desk (`isOpenTrip` is false) and reopens only through the approver move above.
 * An illegal move is a 409 `illegal_trip_transition`.
 */
export const TRIP_TRANSITIONS: Readonly<Record<TravelTripStatus, readonly TravelTripStatus[]>> =
  Object.freeze({
    invited: ['draft', 'withdrawn'],
    draft: ['pending', 'withdrawn', 'approved', 'booked', 'settling'],
    pending: ['approved', 'rejected', 'withdrawn', 'booked', 'settling'],
    approved: ['booked', 'settling', 'needs_action', 'expired', 'cancelled'],
    needs_action: ['booked', 'settling', 'expired', 'cancelled'],
    booked: ['needs_action', 'settling', 'cancelled'],
    settling: ['closed'],
    rejected: ['pending', 'approved', 'booked', 'settling'],
    withdrawn: [],
    cancelled: [],
    expired: [],
    closed: [],
  });

/** Statuses nothing moves on from, and `rejected`, which only an approver's reopen leaves. */
const NOT_OPEN: ReadonlySet<TravelTripStatus> = new Set<TravelTripStatus>([
  'rejected',
  'withdrawn',
  'cancelled',
  'expired',
  'closed',
]);

export function canMoveTrip(from: TravelTripStatus, to: TravelTripStatus): boolean {
  return TRIP_TRANSITIONS[from].includes(to);
}

/** Throws the 409 (`code: 'illegal_trip_transition'`) the API returns for an illegal move. */
export function assertTripMove(from: TravelTripStatus, to: TravelTripStatus): void {
  if (!canMoveTrip(from, to)) {
    throw new ConflictError(`A ${from} trip cannot move to ${to}`, {
      code: 'illegal_trip_transition',
      from,
      to,
    });
  }
}

/**
 * Still live: someone may yet act on it (accept, submit, decide, book, settle). The final
 * statuses — withdrawn, cancelled, expired, closed — and `rejected` are history.
 */
export function isOpenTrip(status: TravelTripStatus): boolean {
  return !NOT_OPEN.has(status);
}

// ---------------------------------------------------------------------------
// Derived phase
// ---------------------------------------------------------------------------

/**
 * Where the trip stands against the calendar on `todayKey`. All three are `YYYY-MM-DD` keys;
 * `todayKey` is today in the traveller's `location.timezone` (the entity's when the location has
 * none, EC-50) — the caller resolves it, this function reads no clock. Both dates are inclusive:
 * the start day and the end day are travelling days.
 */
export function tripPhase(startDate: string, endDate: string, todayKey: string): TravelTripPhase {
  if (todayKey < startDate) return 'upcoming';
  if (todayKey > endDate) return 'returned';
  return 'travelling';
}
